/**
 * SELECT * expansion for the SQL editor's hover: the columns a `*` in a select
 * list stands for, as the engine's own `*` returns them — each USING / NATURAL
 * shared column once, in the engine's order. It is the resolver fold the
 * Visual Query Editor's "Choose columns" writes (`starColumnList` /
 * `starColumns` in sql-resolver/scope/starColumns.ts), read without the
 * "can this be written as SQL" step, so the list also shows where Choose
 * columns is off (an Oracle merged name another table also has).
 *
 * Pure: a parsed buffer, an offset and a schema snapshot in, markdown out.
 * `selectStarHoverProvider.ts` wires it to Monaco (parse + snapshot caches).
 *
 * The star is found through the resolver: the innermost query block around
 * the offset (a CTE body, a subquery, a set-operation branch) and the select
 * item under it, so `COUNT(*)`, `2 * 3` and a `*` in a comment never match.
 * A qualified `t.*` lists the one FROM source bound to `t` (alias, else table
 * name; a schema-qualified `s.t.*` must name the same schema).
 *
 * No hover when the list is not known — a CTE, derived-table or table-function
 * source (the snapshot holds no columns for it), a table the snapshot lacks, a
 * USING column a side lacks, a MySQL `FULL JOIN` (FULL is an alias there, the
 * parser reads a join kind). The hover used to list whatever tables a regex
 * found in the whole buffer, other statements' and CTE bodies' included, so a
 * partial or padded list would mislead more than no list.
 */

import type { ParseOptions, QualifiedName, StatementInfo } from '../../../sql-grammar/model';
import { readQualifiedName } from '../../../sql-resolver/scope/identifiers';
import { bindingKeyOf, querySourceOf } from '../../../sql-resolver/scope/querySources';
import {
  fromItemStarColumns,
  starColumnList,
  type StarColumnSource,
  type StarListColumn,
} from '../../../sql-resolver/scope/starColumns';
import { findSubqueryFramesAt } from '../../../sql-resolver/scope/subqueryScope';
import { extractQueryStructure } from '../../../sql-resolver/structure/extractQueryStructure';
import type { FromItem, ParsedBuffer, QueryStructure, SchemaSnapshot } from '../../../sql-resolver/types';

/** A `*` select item: the query block it belongs to and, for `t.*`, its qualifier. */
export type SelectStarTarget = {
  readonly structure: QueryStructure;
  readonly qualifier?: QualifiedName;
};

/** The `*` select item at `offset` (the offset of the `*` character itself); null for any other `*`. */
export function findSelectStarAt(buffer: ParsedBuffer, offset: number): SelectStarTarget | null {
  const { source, options } = buffer.parsedDocument;
  if (source[offset] !== '*') return null;
  const statement = buffer.documentStructure.statementAt(offset);
  if (!statement) return null;
  let structure: QueryStructure;
  try {
    const frames = findSubqueryFramesAt(buffer, statement.id, offset);
    const frame = frames[frames.length - 1];
    structure = extractQueryStructure(buffer, frame ? nestedSelect(statement, source, frame.innerStart, frame.innerEnd) : statement);
  } catch {
    return null;
  }
  for (const block of [structure, ...(structure.compoundBranches ?? [])]) {
    const item = block.selectItems?.find((i) => i.range.startOffset <= offset && offset < i.range.endOffset);
    if (!item) continue;
    const text = item.expressionText.trim();
    if (text === '*') return { structure: block };
    if (!text.endsWith('.*')) return null;
    const name = readQualifiedName(text, 0, text.length - 2, options);
    return name && name.range.endOffset === text.length - 2 ? { structure: block, qualifier: name.qualified } : null;
  }
  return null;
}

/** The hover's markdown for `target`; null when the columns are not known. */
export function selectStarHoverMarkdown(
  target: SelectStarTarget,
  options: ParseOptions,
  snapshot: SchemaSnapshot | null | undefined,
): string | null {
  const { structure, qualifier } = target;
  const items = fromItemsOf(structure);
  let columns: readonly StarListColumn[] | null;
  if (qualifier) {
    const item = items.find((i) => boundTo(i, qualifier, options));
    columns = item ? fromItemStarColumns(item, options.engine, snapshot) : null;
  } else {
    const list = starColumnList(structure, options, snapshot);
    columns = list.kind === 'columns' ? list.columns : null;
  }
  return columns && columns.length > 0 ? formatHoverMarkdown(columns, items.length > 1) : null;
}

function fromItemsOf(structure: QueryStructure): FromItem[] {
  return [...(structure.fromItems ?? []), ...(structure.joins ?? []).map((j) => j.right)];
}

/** `t` in `t.*` names this FROM item: its binding (alias, else name) and, for `s.t.*`, its schema. */
function boundTo(item: FromItem, qualifier: QualifiedName, options: ParseOptions): boolean {
  const { engine } = options;
  if (querySourceOf(item, engine).bindingKey !== bindingKeyOf(engine, qualifier.foldedName)) return false;
  if (qualifier.foldedSchema === undefined) return true;
  return !item.alias && item.target.kind === 'relation' && item.target.qualified.foldedSchema === qualifier.foldedSchema;
}

/** A synthetic statement over a subquery / CTE body (whitespace trimmed) — the VQE's drilled-body shape. */
function nestedSelect(parent: StatementInfo, source: string, start: number, end: number): StatementInfo {
  while (start < end && /\s/.test(source[start]!)) start++;
  while (end > start && /\s/.test(source[end - 1]!)) end--;
  return {
    ...parent,
    id: `${parent.id}.sub.${start}-${end}`,
    kind: 'select',
    range: { startOffset: start, endOffset: end, startLine: 0, startColumn: 0, endLine: 0, endColumn: 0 },
  };
}

function formatHoverMarkdown(columns: readonly StarListColumn[], multiTable: boolean): string {
  const lines = [`**SELECT \\*** expands to ${columns.length} column${columns.length !== 1 ? 's' : ''}:\n`];
  let currentGroup = '';
  columns.forEach((column, index) => {
    if (multiTable) {
      const group = sourceLabel(groupOf(columns, index));
      if (group !== currentGroup) {
        currentGroup = group;
        lines.push(`\n**${group}**`);
      }
    }
    const shared = column.sharedBy ? ` — shared by ${column.sharedBy.map((s) => escapeMarkdown(s.name)).join(', ')}` : '';
    lines.push(`- \`${column.name}\` *${column.dataType}*${shared}`);
  });
  return lines.join('\n');
}

/**
 * The table a column is listed under. A merged (USING / NATURAL) column goes
 * with its nearest unmerged neighbour — before it, else after it — so the
 * engine's order never splits a table's group (PostgreSQL puts `x` before
 * t1's columns, SQLite in t1's place); its "shared by" note names the tables.
 */
function groupOf(columns: readonly StarListColumn[], index: number): StarColumnSource {
  const column = columns[index]!;
  if (!column.sharedBy) return column.source;
  for (let i = index - 1; i >= 0; i--) if (!columns[i]!.sharedBy) return columns[i]!.source;
  for (let i = index + 1; i < columns.length; i++) if (!columns[i]!.sharedBy) return columns[i]!.source;
  return column.source;
}

function sourceLabel(source: StarColumnSource): string {
  return escapeMarkdown(source.name === source.table ? source.table : `${source.name} (${source.table})`);
}

function escapeMarkdown(text: string): string {
  return text.replace(/[\\`*_[\]<>]/g, '\\$&');
}
